/**
 * voice-v8 — Werkzeugkarten: Anker: zeigen, anlegen, Liste.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { baueKarte, hinweisKarte, kartenTitel } from "../karte.js";
import { trimText, unwrapResult } from "./hilfe.js";

// ─── Ankermomente (Ticket 1614/1678) ───────────────────────────────────────
//
// Ein Ankermoment ist kein Datensatz, den man abliest — er ist ein Bild, das
// an etwas Schoenes erinnert. Darum traegt die Karte das BILD gross und
// darunter genau zwei Zeilen: das Gefuehl und die Situation in Martins
// eigenen Worten. Keine Feldnamen, keine Schluessel-Wert-Liste.
//
// 2026-08-12 (Ticket 1678): Bis hierher gab es fuer die drei Anker-Werkzeuge
// gar keine Karte — sie fielen auf die generische Darstellung zurueck und
// zeigten rohe Felder ("bild_url", "erlebt_am"). Wer in einer dunklen Stunde
// nach einem Anker fragt, soll kein Datenblatt bekommen.
export function buildAnkerCard(tool: string, result: unknown): HTMLElement {
  const r = (unwrapResult(result) ?? {}) as {
    ok?: boolean;
    hinweis?: string;
    image_url?: string;
    gefuehl?: string;
    situation?: string;
    ort?: string | null;
    erlebt_am?: string | null;
    gewaehlt_wegen?: string;
    text?: string;
    gesamt?: number;
  };

  /*
   * ⚠️ 2026-09-03 (Martin: "Ich habe gerade zwei Ankermomente festgehalten.
   * Immer beim Erzeugen steht da unten noch kein Ankermoment.")
   *
   * Hier stand die Leer-Pruefung fuer BEIDE Werkzeuge. `anker_zeigen` liefert
   * bei Erfolg Bild und Gefuehl — `anker_anlegen` aber nicht:
   *
   *     {ok: true, id, gesamt, text: "Festgehalten: …"}
   *
   * Damit war `!image_url && !gefuehl` nach JEDEM erfolgreichen Anlegen wahr,
   * und die Bestaetigung meldete das Gegenteil dessen, was passiert war. In
   * der Datenbank standen die Anker die ganze Zeit korrekt (am 03.09. mit
   * Bild, Gefuehl, Situation und Ort nachgeprueft).
   *
   * Die Leer-Pruefung gilt jetzt nur noch fuers ZEIGEN. Beim Anlegen zaehlt
   * `ok` — und die Karte nimmt den Bestaetigungstext, den das Werkzeug
   * ohnehin mitliefert.
   */
  const istAnlegen = tool === "anker_anlegen";
  // 15.10.2026: der Hinweis ist eine Zeile, kein Absatz (Karten-Größe).
  if (r.ok === false) {
    return hinweisKarte(trimText(r.hinweis || "", 200) || "Das hat gerade nicht geklappt.");
  }
  if (!istAnlegen && !r.image_url && !r.gefuehl) {
    // Wer gerade traurig ist, soll keine Fehlermeldung sehen.
    return hinweisKarte(trimText(r.hinweis || "", 200) || "Noch keine Ankermomente festgehalten.");
  }

  const el = baueKarte("anker");
  el.appendChild(
    kartenTitel(tool === "anker_anlegen" ? "Festgehalten" : "Ankermoment"),
  );

  if (r.image_url) {
    const box = document.createElement("div");
    box.className = "tool-anker-bild";
    const im = document.createElement("img");
    im.src = r.image_url;
    im.alt = r.gefuehl ? String(r.gefuehl) : "Ankermoment";
    im.loading = "lazy";
    // Fehlt das Bild (verschoben, geloescht), bleibt die Karte trotzdem
    // brauchbar — Gefuehl und Situation tragen sie auch allein.
    im.addEventListener("error", () => box.remove());
    box.appendChild(im);
    el.appendChild(box);
  }

  /*
   * ⚠️ 07.09.2026 — die Karte wiederholt den Text nicht mehr in voller Laenge.
   *
   * Martin, mit Bild: *„Dann ist gestanden: Suche laeuft noch … und darunter
   * war der Text noch einmal. Muss der Text zweimal dastehen?"*
   *
   * Nein. Er steht schon an der Bildblase, die er selbst geschickt hat. Die
   * Karte ist die BESTAETIGUNG, nicht die Zweitschrift. Dass er hier
   * ueberhaupt in voller Laenge stand, hat einen Grund: Die Vorlage im
   * Eingabefeld endet auf „Gefuehl: ", und was er danach diktiert, landet
   * ganz in diesem Feld — an dem Tag waren das 380 Zeichen.
   *
   * Gekuerzt statt weggelassen: Ein Gefuehl ist der Kern des Ankers, es soll
   * schon zu sehen sein — aber als Zeile, nicht als Absatz.
   */
  const kurz = (t: string, n: number): string =>
    t.length <= n ? t : `${t.slice(0, n).replace(/\s+\S*$/, "")} …`;
  const gefuehl = r.gefuehl ? String(r.gefuehl).trim() : "";
  if (gefuehl) {
    const g = document.createElement("div");
    g.className = "tool-anker-gefuehl";
    g.textContent = kurz(gefuehl, 110);
    // Der volle Wortlaut bleibt erreichbar, ohne die Karte zu fluten.
    if (gefuehl.length > 110) g.title = gefuehl;
    el.appendChild(g);
  }

  if (r.situation) {
    const situation = String(r.situation).trim();
    /*
     * ⚠️ Nicht zweimal dasselbe. Beim Anlegen aus einer Bildunterschrift
     *    tragen Gefuehl und Situation oft denselben Satz — dann ist die
     *    zweite Zeile reine Wiederholung.
     */
    const dopplung =
      situation === gefuehl ||
      (gefuehl.length > 0 && gefuehl.startsWith(situation.slice(0, 40)));
    if (!dopplung) {
      const sit = document.createElement("div");
      sit.className = "tool-anker-situation";
      sit.textContent = kurz(situation, 160);
      if (situation.length > 160) sit.title = situation;
      el.appendChild(sit);
    }
  }

  // Ort und Datum gehoeren zusammen in EINE leise Zeile — sie sind Beiwerk,
  // nicht die Aussage.
  const bei: string[] = [];
  if (r.ort) bei.push(trimText(String(r.ort), 80));
  if (r.erlebt_am) bei.push(trimText(String(r.erlebt_am), 40));
  if (bei.length) {
    const meta = document.createElement("div");
    meta.className = "tool-anker-meta";
    meta.textContent = bei.join(" · ");
    el.appendChild(meta);
  }

  // Warum GERADE dieser Anker kam — das Werkzeug waehlt nach Martins Zustand
  // (Stimmung + WHOOP), und ohne diesen Satz wirkt die Wahl beliebig.
  /*
   * Beim Anlegen liefert das Werkzeug weder Bild noch Gefuehl, sondern einen
   * Bestaetigungssatz und die neue Gesamtzahl. Ohne diese Zeilen bliebe die
   * Karte nach der Reparatur der Leer-Pruefung fast leer — ein zweiter
   * stiller Fehler an derselben Stelle.
   */
  if (istAnlegen) {
    if (r.text) {
      const t = document.createElement("p");
      t.className = "tool-anker-situation";
      // Die Bestätigung ist ein Satz — der Anker steht schon an der Bildblase (07.09.2026).
      t.textContent = trimText(String(r.text), 300);
      el.appendChild(t);
    }
    if (typeof r.gesamt === "number") {
      const z = document.createElement("p");
      z.className = "tool-anker-meta";
      z.textContent =
        r.gesamt === 1
          ? "Dein erster Ankermoment."
          : `${r.gesamt} Ankermomente gesammelt.`;
      el.appendChild(z);
    }
  }
  if (r.gewaehlt_wegen) {
    const w = document.createElement("div");
    w.className = "tool-anker-warum";
    w.textContent = `Gewählt für: ${trimText(String(r.gewaehlt_wegen), 200)}`;
    el.appendChild(w);
  }

  return el;
}

export function buildAnkerListeCard(result: unknown): HTMLElement {
  const r = (unwrapResult(result) ?? {}) as {
    anzahl?: number;
    momente?: {
      id?: string;
      gefuehl?: string;
      situation?: string;
      ort?: string | null;
      image_url?: string;
    }[];
  };
  const liste = Array.isArray(r.momente) ? r.momente : [];
  if (liste.length === 0) {
    return hinweisKarte("Noch keine Ankermomente festgehalten.");
  }

  const el = baueKarte("anker-liste");
  el.appendChild(
    kartenTitel(
      `${r.anzahl ?? liste.length} Ankermoment${
        (r.anzahl ?? liste.length) === 1 ? "" : "e"
      }`,
    ),
  );

  const grid = document.createElement("div");
  grid.className = "tool-anker-grid";
  for (const m of liste) {
    const kachel = document.createElement("div");
    kachel.className = "tool-anker-kachel";
    if (m.image_url) {
      const im = document.createElement("img");
      im.src = String(m.image_url);
      im.alt = m.gefuehl ? String(m.gefuehl) : "";
      im.loading = "lazy";
      im.addEventListener("error", () => im.remove());
      kachel.appendChild(im);
    }
    if (m.gefuehl) {
      const g = document.createElement("span");
      g.className = "tool-anker-kachel-gefuehl";
      g.textContent = String(m.gefuehl);
      kachel.appendChild(g);
    }
    grid.appendChild(kachel);
  }
  el.appendChild(grid);
  return el;
}
